import React, { useCallback, useMemo, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { DataTable, type SortingState } from "@racoon/ui";
import type { Row } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { adaptTrucksByProvider, type TruckRow } from "../lib/adapt-trucks-by-provider";
import { useTruckColumns } from "../lib/use-truck-columns";

type GovTruckInfo = ConnectProto.GovTruckInfo;

interface TrucksTableProps {
  trucks: GovTruckInfo[];
  isFetching?: boolean;
  /** Highlighted row, kept in sync with whatever the page considers selected. */
  selectedTruckId?: string | null;
  /** Opens a truck's record. Reached through the plate-number link, not the row. */
  onTruckClick?: (truckId: string) => void;
}

/**
 * Desktop trucks registry: one collapsible section per MVO (service provider).
 *
 * Mirrors the grouped-table pattern from admin's job map — a flat row list where
 * group headers are swapped in via `renderCustomRow`, children of collapsed
 * groups are filtered out of `data`, and sorting runs manually so headers stay
 * pinned at the top of their own group.
 */
export const TrucksTable: React.FC<TrucksTableProps> = ({
  trucks,
  isFetching,
  selectedTruckId,
  onTruckClick,
}) => {
  const t = useT();

  const handleDetails = useCallback((truckId: string) => onTruckClick?.(truckId), [onTruckClick]);
  const columns = useTruckColumns({ onDetails: handleDetails });

  // Groups are collapsed by default, so this map only ever holds the groups the
  // user has explicitly opened. Absent key === collapsed.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const unknownProviderLabel = "—";

  const allRows = useMemo(
    () => adaptTrucksByProvider(trucks, unknownProviderLabel),
    [trucks, unknownProviderLabel],
  );

  const isGroupExpanded = useCallback(
    (row: TruckRow) => expanded[row._groupKey] ?? false,
    [expanded],
  );

  const handleToggleGroup = useCallback((row: TruckRow) => {
    setExpanded((prev) => ({ ...prev, [row._groupKey]: !(prev[row._groupKey] ?? false) }));
  }, []);

  // Sort trucks inside each group with the active column's accessor; the group
  // header always stays at index 0 of its own run.
  const sortedRows = useMemo((): TruckRow[] => {
    if (!sorting.length) return allRows;
    const [{ id: colId, desc }] = sorting;
    const col = columns.find((c) => c.id === colId);
    if (!col?.accessor) return allRows;
    const getValue = col.accessor;

    const groups: TruckRow[][] = [];
    for (const row of allRows) {
      if (row._isGroupHeader) {
        groups.push([row]);
      } else {
        if (groups.length === 0) groups.push([]);
        groups[groups.length - 1].push(row);
      }
    }

    for (const group of groups) {
      const children = group.slice(1);
      children.sort((a, b) => {
        const av = getValue(a, 0);
        const bv = getValue(b, 0);
        if (av == null && bv == null) return 0;
        if (av == null) return desc ? -1 : 1;
        if (bv == null) return desc ? 1 : -1;
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return desc ? -cmp : cmp;
      });
      // Re-mark the group's last row so the rounded bottom border follows the
      // sort. Copies, never mutations: `allRows` is a memo that gets returned
      // as-is when sorting is cleared, and stale flags would round the wrong row.
      const reflagged = children.map((child, index) => ({
        ...child,
        _isLastInGroup: index === children.length - 1,
      }));
      group.splice(1, children.length, ...reflagged);
    }

    return groups.flat();
  }, [allRows, sorting, columns]);

  // Drop the children of collapsed groups before handing data to the table.
  const tableData = useMemo(() => {
    const result: TruckRow[] = [];
    let currentGroupExpanded = false;
    for (const row of sortedRows) {
      if (row._isGroupHeader) {
        currentGroupExpanded = isGroupExpanded(row);
        result.push(row);
      } else if (currentGroupExpanded) {
        result.push(row);
      }
    }
    return result;
  }, [sortedRows, isGroupExpanded]);

  const selectedKeys = useMemo(() => (selectedTruckId ? [selectedTruckId] : []), [selectedTruckId]);

  const renderCustomRow = useCallback(
    (row: Row<TruckRow>, index: number, visibleColumnCount: number) => {
      const r = row.original;
      if (!r._isGroupHeader) return undefined;
      const isExpanded = isGroupExpanded(r);

      // Figma node 18705:12220 — 48px tall, 16px padding, 16px gap, a 16px
      // dropdown chevron, the MVO name in Inter Bold 14/14 on Text/body, and a
      // Surface/information (#2563eb) pill with the MVO's truck count. The design
      // shows a "<on line>/<total>" fraction, but no endpoint can supply the
      // numerator per MVO yet, so the pill shows the total alone.
      return (
        <React.Fragment key={`group-${r._groupKey}`}>
          {/* The design separates group blocks with an 8px flex gap. A table
              cannot do that, so an empty transparent row stands in for it —
              padding would sit inside the white cell and never show. */}
          <tr aria-hidden className="pointer-events-none h-2 border-0 bg-transparent">
            <td colSpan={visibleColumnCount} className="border-0 bg-transparent p-0" />
          </tr>
          <tr className="group-header-row">
            <td
              role="button"
              tabIndex={0}
              onClick={() => handleToggleGroup(r)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleToggleGroup(r);
                }
              }}
              colSpan={visibleColumnCount}
              className={`h-12 cursor-pointer bg-white p-0 ${
                isExpanded ? "rounded-t-lg" : "rounded-lg"
              }`}
            >
              <div className="flex h-full items-center gap-4 p-4">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 text-text-body"
                  style={{
                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.15s",
                  }}
                  aria-hidden
                >
                  <path d="M4 6l4 4 4-4" />
                </svg>

                <span className="truncate p-1 text-sm font-bold leading-[14px] tracking-[0.28px] text-text-body">
                  {r._groupProviderName}
                </span>

                <span className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-blue-600 px-2 py-1 text-xs font-bold leading-[14px] text-white">
                  {r._groupTotalCount}
                </span>
              </div>
            </td>
          </tr>
        </React.Fragment>
      );
    },
    [handleToggleGroup, isGroupExpanded],
  );

  const rowClassName = useCallback(
    (row: TruckRow) => {
      if (row._isGroupHeader) return "";
      // Figma node 18708:12614 — a 48px white row whose divider is its OWN
      // top border, so the line lands between every pair of rows (and under the
      // group header) while the last row's bottom stays clean.
      // Rows are inert — only the plate-number link navigates — so no pointer
      // cursor or hover tint that would imply the whole row is clickable.
      let base = [
        "[&>td]:h-12 [&>td]:bg-white [&>td]:p-2",
        "[&>td]:border-t [&>td]:border-border-primary",
        // The plate cell is px-8/py-12 in the design, not a flat 8px.
        "[&>td:first-child]:px-2 [&>td:first-child]:py-3",
      ].join(" ");
      // Rounds off the white group block, mirroring the header's rounded top.
      if (row._isLastInGroup) {
        base += " [&>td:first-child]:rounded-bl-lg [&>td:last-child]:rounded-br-lg";
      }
      const isSelected = String(row.id) === String(selectedTruckId);
      return isSelected ? `${base} [&>td]:!bg-blue-50` : base;
    },
    [selectedTruckId],
  );

  // DataTableColumnHeader's double-click-to-fit measures `row.children[thIndex]`
  // across every body row. Our group-header and spacer rows are a single
  // `<td colSpan>`, so for the first column that full-width cell gets measured as
  // if it were a plate number and the column snaps to its width. Swallowing the
  // gesture in the capture phase disables auto-fit for this table only — both the
  // header's `onDoubleClick` and the resize handle's `onMouseDown` (which routes
  // to the same handler at `detail === 2`) are bubble handlers on descendants.
  const swallowAutoFit = useCallback((event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest("th")) event.stopPropagation();
  }, []);

  return (
    <div
      className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
      onDoubleClickCapture={swallowAutoFit}
      onMouseDownCapture={(event) => {
        if (event.detail === 2) swallowAutoFit(event);
      }}
    >
      <DataTable
        data={tableData}
        columns={columns}
        rowKey={(row) => row.id}
        namespace={Namespace.Govdash}
        loading={isFetching}
        renderCustomRow={renderCustomRow}
        manualSorting
        onSortChange={setSorting}
        selectedRowKeys={selectedKeys}
        rowClassName={rowClassName}
        empty={
          <div className="p-6 text-center text-sm text-text-secondary">
            {t(Namespace.Common, "noResults")}
          </div>
        }
        // Figma node 18705:12170 — the header is a 40px bar bordered all round
        // with an 8px radius, cells 8px/16px, sticky; the body sits flush under it.
        className={[
          "h-full !border-none bg-transparent",
          "[&_table]:border-separate [&_table]:border-spacing-0",
          "[&_thead]:bg-transparent [&_thead]:shadow-none",
          "[&_th]:h-10 [&_th]:bg-white [&_th]:border-y [&_th]:border-border-primary",
          // The design insets header labels by 16px — 8px cell padding plus its
          // always-present 16px sort caret. DataTableColumnHeader only renders
          // that caret once a column is sorted, so the inset lives on the cell.
          "[&_th]:pl-4 [&_th]:pr-2",
          "[&_th:first-child]:rounded-l-lg [&_th:first-child]:border-l",
          "[&_th:last-child]:rounded-r-lg [&_th:last-child]:border-r",
        ].join(" ")}
      />
    </div>
  );
};
